import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import type { Mesh } from "three";

interface SceneOrbitsProps {
  /** World position of the parent star. */
  center: [number, number, number];
  hue?: number;
}

/**
 * Cosmos scene setup: two small planets (title scene + end scene) that
 * orbit the newly born star. They fade in over ~1s and then orbit slowly.
 */
export function SceneOrbits({ center, hue = 210 }: SceneOrbitsProps) {
  return (
    <group position={center}>
      <OrbitingPlanet
        radius={2.2}
        speed={0.4}
        size={0.22}
        hue={hue + 30}
        phaseOffset={0}
      />
      <OrbitingPlanet
        radius={3.0}
        speed={-0.28}
        size={0.18}
        hue={hue - 40}
        phaseOffset={Math.PI}
      />
      <OrbitRing radius={2.2} hue={hue} />
      <OrbitRing radius={3.0} hue={hue} />
    </group>
  );
}

export interface ChoiceOrbitsProps {
  center: [number, number, number];
  hue?: number;
  choiceIds: string[];
}

/**
 * One orbiting planet per outgoing choice from the focused scene star.
 * Each choice gets a unique radius, speed, and hue offset.
 */
export function ChoiceOrbits({ center, hue = 210, choiceIds }: ChoiceOrbitsProps) {
  return (
    <group position={center}>
      {choiceIds.map((choiceId, index) => {
        const radius = 2.0 + index * 0.65;
        const speed = 0.36 + index * 0.11 * (index % 2 === 0 ? 1 : -1);
        const size = 0.16 + (index % 3) * 0.04;
        const hueOffset = index * 28 - 20;
        const phaseOffset = (index / Math.max(choiceIds.length, 1)) * Math.PI * 2;

        return (
          <group key={choiceId}>
            <OrbitingPlanet
              radius={radius}
              speed={speed}
              size={size}
              hue={hue + hueOffset}
              phaseOffset={phaseOffset}
            />
            <OrbitRing radius={radius} hue={hue + hueOffset * 0.5} />
          </group>
        );
      })}
    </group>
  );
}

function OrbitingPlanet({
  radius,
  speed,
  size,
  hue,
  phaseOffset,
}: {
  radius: number;
  speed: number;
  size: number;
  hue: number;
  phaseOffset: number;
}) {
  const ref = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const mesh = ref.current;
    if (!mesh) return;

    const fadeIn = Math.min(1, t / 1.0);
    const angle = clock.elapsedTime * speed + phaseOffset;
    mesh.position.x = Math.cos(angle) * radius;
    mesh.position.z = Math.sin(angle) * radius;
    mesh.position.y = Math.sin(angle * 1.3) * 0.3;
    mesh.scale.setScalar(fadeIn);
  });

  const color = `hsl(${((hue % 360) + 360) % 360}, 72%, 62%)`;

  return (
    <mesh ref={ref} scale={0}>
      <sphereGeometry args={[size, 12, 12]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.35}
        roughness={0.35}
        metalness={0.25}
      />
    </mesh>
  );
}

function OrbitRing({ radius, hue }: { radius: number; hue: number }) {
  const ref = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const mesh = ref.current;
    if (!mesh) return;
    (mesh.material as { opacity: number }).opacity = Math.min(0.18, t * 0.18);
  });

  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[radius - 0.01, radius + 0.01, 64]} />
      <meshBasicMaterial
        color={`hsl(${hue}, 58%, 72%)`}
        transparent
        opacity={0}
        toneMapped={false}
      />
    </mesh>
  );
}
